<script setup lang="ts">
import { motion } from 'motion-v'
import type { VariantType } from 'motion-v'
import { useWindowScroll } from '@vueuse/core'

// 1. On va chercher les données du YAML
const { data: page } = await useAsyncData('global-nav', () => queryCollection('content').first())

const nuxtApp = useNuxtApp()
const activeSection = ref<string>()

const { y } = useWindowScroll()
const showHeaderLogo = computed(() => y.value > 150)

// 2. On génère les liens dynamiquement depuis le YAML
const items = computed(() => {
  return (page.value?.navigation || []).map((navItem: any) => ({
    label: navItem.label,
    to: navItem.to,
    exactHash: true,
    // La pastille s'active si on est sur la bonne section ou en haut de page
    active: navItem.id === 'home'
      ? (!activeSection.value || activeSection.value === 'home')
      : activeSection.value === navItem.id
  }))
})

// 3. Scroll Spy intelligent (mise à jour)
nuxtApp.hooks.hookOnce('page:loading:end', () => {
  const observer = new IntersectionObserver((entries) => {
    const visible = entries.find(e => e.isIntersecting)
    if (visible) {
      activeSection.value = visible.target.id
    }

    // Sécurité : si on remonte tout en haut, on force "Accueil"
    if (window.scrollY < 100) {
      activeSection.value = 'home'
    }
  }, {
    // On élargit la zone de détection pour que ça s'allume plus facilement
    rootMargin: '-30% 0px -60% 0px'
  })

  // On observe dynamiquement toutes les ancres (#) de ton YAML
  const sectionsToObserve = (page.value?.navigation || [])
    .filter((n: any) => n.to.startsWith('#'))
    .map((n: any) => n.to.substring(1))

  sectionsToObserve.forEach((id: string) => {
    const el = document.getElementById(id)
    if (el) observer.observe(el)
  })
})

const variants: Record<string, VariantType | ((custom: unknown) => VariantType)> = {
  normal: { rotate: 0, y: 0, opacity: 1 },
  close: (custom: unknown) => {
    const c = custom as number
    return {
      rotate: c === 1 ? 45 : c === 3 ? -45 : 0,
      y: c === 1 ? 6 : c === 3 ? -6 : 0,
      opacity: c === 2 ? 0 : 1,
      transition: { type: 'spring', stiffness: 260, damping: 20 }
    }
  }
}
</script>

<template>
  <UHeader>
    <template #left>
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-4"
      >
        <NuxtLink v-show="showHeaderLogo" to="/" class="flex items-center">
          <img src="/logo.webp" alt="Ohmelec - Électricité Générale et Sécurité" width="300" height="263" class="h-14 w-auto" />        </NuxtLink>
      </Transition>
    </template>

    <UNavigationMenu :items="items" variant="link" />

    <template #toggle="{ open, toggle, ui }">
      <UButton
        size="sm"
        variant="ghost"
        color="neutral"
        square
        :aria-label="open ? 'Close navigation' : 'Open navigation'"
        :aria-expanded="open"
        :class="ui.toggle({ toggleSide: 'right' })"
        @click="toggle"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <motion.line x1="4" y1="6" x2="20" y2="6" :variants="variants" :animate="open ? 'close' : 'normal'" :custom="1" class="outline-none" />
          <motion.line x1="4" y1="12" x2="20" y2="12" :variants="variants" :animate="open ? 'close' : 'normal'" :custom="2" class="outline-none" />
          <motion.line x1="4" y1="18" x2="20" y2="18" :variants="variants" :animate="open ? 'close' : 'normal'" :custom="3" class="outline-none" />
        </svg>
      </UButton>
    </template>

    <template #body>
      <UNavigationMenu :items="items" orientation="vertical" />
    </template>
  </UHeader>
</template>
